import { useTranslation } from 'react-i18next'

import { toastFactory } from '@/factories'
import { formatBytes } from '@/utils/file/format-bytes'

import { INVALID_FILE_CONTENT_ERROR } from './file-upload.constants'
import { ValidationError } from './file-upload.types'

interface UseImageUploadErrorToastOptions {
  acceptedTypes: string[]
  maxFileSize: number
  maxOutputFileSize?: number
}

const normalizeType = (type: string) => type.toUpperCase().replace('.', '')

const formatLimit = (bytes: number) =>
  bytes % 1_000_000 === 0 ? `${bytes / 1_000_000} MB` : formatBytes(bytes)

export const useImageUploadErrorToast = ({
  acceptedTypes,
  maxFileSize,
  maxOutputFileSize,
}: UseImageUploadErrorToastOptions) => {
  const { t } = useTranslation(['common'])

  return ([first]: ValidationError[]) => {
    if (!first) return

    const { name, size } = first.file
    const extension = (name.split('.').pop() ?? '').toUpperCase()
    const normalized = acceptedTypes.map(normalizeType)

    if (first.errors.includes(INVALID_FILE_CONTENT_ERROR)) {
      toastFactory.error(t('common:fileUpload.imageErrors.invalidContent'))
    } else if (!normalized.includes(extension)) {
      const types = normalized
        .filter((type) => type !== 'JPEG' || !normalized.includes('JPG'))
        .join(', ')

      toastFactory.error(t('common:fileUpload.imageErrors.unsupportedType', { types }))
    } else if (size > maxFileSize) {
      toastFactory.error(
        t('common:fileUpload.imageErrors.tooLarge', {
          max: formatLimit(maxFileSize),
          size: formatBytes(size),
        }),
      )
    } else {
      toastFactory.error(
        t('common:fileUpload.imageErrors.tooLargeAfterResize', {
          max: formatLimit(maxOutputFileSize ?? maxFileSize),
        }),
      )
    }
  }
}
